import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Divider from '@material-ui/core/Divider'

import { Dialog, GooglePlacesAutocompleteV2, snackbar } from 'components'
import CustomTextField from 'components/CustomTextField'
import { Place } from 'components/GooglePlacesAutocompleteV2/types'
import { useCheckFormErrors, useApi, useApiCall } from 'hooks'
import { ERRORS } from 'consts'
import { ApiError } from 'errors'
import { ConfigUtils } from 'utils/config'

import {
  updateStore,
  getExternalApiPartners,
  getStorePartnerStores,
  upsertStorePartnerStore,
} from '../api'
import {
  UpdateStoreData,
  Store,
  ExternalApiPartner,
  StorePartnerStore,
  UpsertPartnerStorePayload,
} from '../types'

const rules = {
  address: [],
  googlePlaceId: [],
  name: [
    {
      validate: (name: string) => name.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  location: [],
}

interface PartnerStoreMappingSectionProps {
  storeId: number
}

interface UpsertPartnerStoreRequest {
  storeId: number
  partnerStore: UpsertPartnerStorePayload
}

const PartnerStoreMappingSection: React.FC<PartnerStoreMappingSectionProps> = ({ storeId }) => {
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | ''>('')
  const [partnerStoreIdInput, setPartnerStoreIdInput] = useState('')

  const [upsertPartnerStoreApi, isUpserting] = useApiCall<
    UpsertPartnerStoreRequest,
    { partnerStore: StorePartnerStore }
  >(({ storeId: id, partnerStore }) => upsertStorePartnerStore(id, partnerStore))

  const [{ data: partners }] = useApi<{ partners: ExternalApiPartner[] }, ExternalApiPartner[]>(
    getExternalApiPartners,
    response => response?.partners ?? [],
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )

  const [{ data: partnerStores }, , setPartnerStores] = useApi<
    { partnerStores: StorePartnerStore[] },
    StorePartnerStore[]
  >(
    () => getStorePartnerStores(storeId),
    response => response?.partnerStores ?? [],
    {
      baseData: [],
      deps: [storeId],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const handlePartnerSelect = (partnerId: number | '') => {
    setSelectedPartnerId(partnerId)
    if (partnerId === '') {
      setPartnerStoreIdInput('')
      return
    }
    const existing = partnerStores.find(m => m.externalApiUserId === partnerId)
    setPartnerStoreIdInput(existing ? String(existing.partnerStoreId) : '')
  }

  const canSaveMapping =
    selectedPartnerId !== '' &&
    partnerStoreIdInput.trim() !== '' &&
    Number.isInteger(Number(partnerStoreIdInput))

  const handleSavePartnerMapping = async () => {
    if (selectedPartnerId === '' || !canSaveMapping) return
    try {
      const { partnerStore } = await upsertPartnerStoreApi({
        storeId,
        partnerStore: {
          externalApiUserId: selectedPartnerId,
          partnerStoreId: Number(partnerStoreIdInput),
        },
      })
      setPartnerStores(prev => [
        ...prev.filter(m => m.externalApiUserId !== partnerStore.externalApiUserId),
        partnerStore,
      ])
      snackbar.show('Mapeo de partner guardado')
    } catch (err) {
      console.error(err)
      if (err instanceof ApiError && err.message?.includes('partnerStoreId')) {
        snackbar.show('Ese ID de partner ya está asignado a otro local')
        return
      }
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  return (
    <>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1">IDs de partners</Typography>
      </Grid>

      {partnerStores.length > 0 && (
        <Grid item xs={12}>
          <List dense>
            {partnerStores.map(mapping => (
              <ListItem key={mapping.id} disableGutters>
                <ListItemText
                  primary={`${mapping.partner || `#${mapping.externalApiUserId}`}: ${
                    mapping.partnerStoreId
                  }`}
                />
              </ListItem>
            ))}
          </List>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel id="partner-select-label">Partner</InputLabel>
          <Select
            labelId="partner-select-label"
            value={selectedPartnerId}
            onChange={e => handlePartnerSelect(e.target.value as number | '')}
          >
            {partners.map(partner => (
              <MenuItem key={partner.id} value={partner.id}>
                {partner.partner}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <CustomTextField
          fullWidth
          required
          label="ID store partner"
          type="number"
          value={partnerStoreIdInput}
          onChange={e => setPartnerStoreIdInput(e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="outlined"
          color="primary"
          disabled={!canSaveMapping || isUpserting}
          onClick={handleSavePartnerMapping}
        >
          Guardar mapeo
        </Button>
      </Grid>
    </>
  )
}

interface UpdateStoreDialogProps {
  store: Store
  handleClose: () => void
  onDone: (newStore: Store) => void
}

const UpdateStoreDialog: React.FC<UpdateStoreDialogProps> = ({ store, handleClose, onDone }) => {
  const [address, setAddress] = useState(store.address)
  const [name, setName] = useState(store.name)
  const [location, setLocation] = useState<[number, number]>(store.location)
  const [googlePlaceId, setGooglePlaceId] = useState(store.googlePlaceId ?? '')

  const data = { address, name, location, googlePlaceId }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(data, rules, [
    'name',
    'address',
    'location',
  ])
  const isSubmitDisabled = isAnyFieldEmpty || hasErrors

  const [updateStoreApi, isLoading] = useApiCall<UpdateStoreData, void>(updateStore)
  const isTucanEnabled = ConfigUtils.integrations.tucan.enabled

  const onSelectedPlace = (place: Place) => {
    setAddress(place.address)
    setLocation([place.lat, place.lng])
  }

  const handleSubmit = async () => {
    try {
      await updateStoreApi({ id: store.id, payload: data })
      onDone({ id: store.id, address, name, location, googlePlaceId })
    } catch (err) {
      console.error(err)
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  return (
    <Dialog
      title="Editar local"
      isOpen
      onCancel={handleClose}
      showActions
      okButtonText="Editar local"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={handleSubmit}
      isLoading={isLoading}
      contentStyle={{ minWidth: 500 }}
      overflow
      style={{ minHeight: '40%' }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CustomTextField
            fullWidth
            label="Nombre"
            required
            onChange={e => setName(e.target.value)}
            value={name}
            error={errors.name.hasError}
            helperText={errors.name.message}
            maxLength={100}
          />
        </Grid>
        <Grid item xs={12}>
          <GooglePlacesAutocompleteV2
            onSelect={onSelectedPlace}
            required
            label="Dirección"
            placeholder="Buscar dirección..."
            value={address}
            onChange={setAddress}
            error={errors.address.hasError}
            helperText={errors.address.message}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTextField
            fullWidth
            label="Google Place ID (opcional, para importar reseñas)"
            value={googlePlaceId}
            onChange={e => setGooglePlaceId(e.target.value)}
            helperText={
              store.googleLocationResourceName
                ? 'Vinculado a Google Business Profile (se actualiza al sincronizar reseñas)'
                : 'Debe coincidir con el Place ID del local en Google para importar reseñas'
            }
          />
        </Grid>

        {isTucanEnabled && <PartnerStoreMappingSection storeId={store.id} />}
      </Grid>
    </Dialog>
  )
}

export { UpdateStoreDialog }
